import type { FieldErrors, Resolver } from "react-hook-form";
import { evaluateFieldRules } from "./ruleEvaluator";
import type { FieldValidationRules, FormFieldDeclarations, Reactive, ResolvedFieldValidationRules } from "./types";

type DeclarativeResolverRuntimeOptions = {
    __businessForm?: {
        validateDisabledFields?: boolean;
    };
};

type RuntimeArrayItemDeclaration<TContext = void> = {
    type?: "string" | "number" | "boolean" | "array" | "object" | "nullable" | "date";
    validation?: FieldValidationRules<Record<string, unknown>, TContext, unknown>;
    disabled?: (values: Record<string, unknown>, context: TContext) => boolean;
    visible?: (values: Record<string, unknown>, context: TContext) => boolean;
};

function getReactiveValue<TForm extends object, TValue, TContext = void>(
    input: Reactive<TForm, TValue, TContext> | undefined,
    values: TForm,
    context: TContext,
): TValue | undefined {
    if (typeof input === "function") {
        return (input as (v: TForm, ctx: TContext) => TValue)(values, context);
    }
    return input;
}

function inferDeclarationType(value: unknown): "string" | "number" | "boolean" | "array" | "object" | "nullable" | "date" {
    if (Array.isArray(value)) return "array";
    if (value === null) return "nullable";
    if (typeof value === "number") return "number";
    if (typeof value === "boolean") return "boolean";
    if (value != null && typeof value === "object") return "object";
    return "string";
}

function setNestedError(
    errors: FieldErrors<Record<string, unknown>>,
    arrayField: string,
    index: number,
    itemField: string,
    fieldError: NonNullable<FieldErrors<Record<string, unknown>>[string]>,
): void {
    const arrayErrors = errors[arrayField];
    const normalizedArrayErrors: Array<Record<string, unknown>> = Array.isArray(arrayErrors)
        ? (arrayErrors as Array<Record<string, unknown>>)
        : [];
    normalizedArrayErrors[index] ??= {};
    normalizedArrayErrors[index][itemField] = fieldError;
    errors[arrayField] = normalizedArrayErrors as FieldErrors<Record<string, unknown>>[string];
}

/**
 * Резолвит reactive-правила поля в статический набор на текущий snapshot формы.
 * @template TForm тип значений формы
 * @template TContext тип контекста
 * @param validation правила из декларации поля
 * @param values текущее состояние формы
 * @returns полностью вычисленные правила для текущего `values`
 */
export function resolveReactiveValidationRules<TForm extends object, TContext = void, TValue = unknown>(
    validation: FieldValidationRules<TForm, TContext, TValue> | undefined,
    values: TForm,
    context: TContext,
): ResolvedFieldValidationRules<TContext> | undefined {
    if (validation == null) return undefined;
    return {
        required: getReactiveValue(validation.required, values, context),
        minLength: getReactiveValue(validation.minLength, values, context),
        maxLength: getReactiveValue(validation.maxLength, values, context),
        min: getReactiveValue(validation.min, values, context),
        max: getReactiveValue(validation.max, values, context),
        integer: getReactiveValue(validation.integer, values, context),
        positive: getReactiveValue(validation.positive, values, context),
        pattern: getReactiveValue(validation.pattern, values, context),
        validate: validation.validate as ResolvedFieldValidationRules<TContext>["validate"],
    };
}

/**
 * Строит `react-hook-form` resolver из деклараций `defineFormConfig`.
 * @template TForm тип значений формы
 * @template TDomain тип доменной модели
 * @param declarations декларации полей формы
 * @returns async resolver, который валидирует поля в порядке деклараций
 */
export function createDeclarativeResolver<TForm extends object, TDomain extends object, TContext = void>(
    declarations: FormFieldDeclarations<TForm, TDomain, TContext>,
): Resolver<Record<string, unknown>, TContext> {
    return async (values, context, options) => {
        const formValues = values as TForm;
        const resolverContext = (context ?? (undefined as TContext)) as TContext;
        const runtimeOptions = (options ?? {}) as DeclarativeResolverRuntimeOptions;
        const validateDisabledFields = runtimeOptions.__businessForm?.validateDisabledFields === true;
        const errors: FieldErrors<Record<string, unknown>> = {};
        for (const key of Object.keys(declarations) as (keyof TForm)[]) {
            const declaration = declarations[key];
            const resolvedRules = resolveReactiveValidationRules(declaration.validation, formValues, resolverContext);
            const fieldValue = (values as Record<string, unknown>)[key as string];
            const fieldError = await evaluateFieldRules(
                declaration,
                resolvedRules,
                fieldValue,
                formValues,
                resolverContext,
                {
                    validateDisabledFields,
                },
            );
            if (fieldError != null) {
                errors[key as string] = fieldError;
            }

            if (declaration.type !== "array" || declaration.itemFields == null || !Array.isArray(fieldValue)) {
                continue;
            }
            const itemFields = declaration.itemFields as Record<string, RuntimeArrayItemDeclaration<TContext>>;
            for (let index = 0; index < fieldValue.length; index++) {
                const rawItem = fieldValue[index];
                const itemValues =
                    rawItem != null && typeof rawItem === "object"
                        ? (rawItem as Record<string, unknown>)
                        : ({} as Record<string, unknown>);
                for (const itemKey of Object.keys(itemFields)) {
                    const itemDeclaration = itemFields[itemKey];
                    if (itemDeclaration == null) continue;
                    const itemRules = resolveReactiveValidationRules(
                        itemDeclaration.validation,
                        itemValues,
                        resolverContext,
                    );
                    const itemValue = itemValues[itemKey];
                    const normalizedItemDeclaration = {
                        ...itemDeclaration,
                        type: itemDeclaration.type ?? inferDeclarationType(itemValue),
                    };
                    const itemError = await evaluateFieldRules(
                        normalizedItemDeclaration as Parameters<typeof evaluateFieldRules>[0],
                        itemRules,
                        itemValue,
                        itemValues,
                        resolverContext,
                        {
                            validateDisabledFields,
                        },
                    );
                    if (itemError != null) {
                        setNestedError(
                            errors,
                            key as string,
                            index,
                            itemKey,
                            itemError as NonNullable<FieldErrors<Record<string, unknown>>[string]>,
                        );
                    }
                }
            }
        }
        if (Object.keys(errors).length > 0) {
            return { values: {}, errors };
        }
        return { values, errors: {} };
    };
}
